/**
 * PolicyLibraryPage.js
 * 
 * Unified Policy Library with NAVI AI-powered metadata extraction.
 * Mirrors the Regulatory Library UI but for internal policies.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import {
  Files, Plus, Upload, Trash, Eye, Warning, CheckCircle, Clock, Calendar, 
  User, X, Brain, Sparkle, MagnifyingGlass, FileText, ArrowClockwise, 
  CaretDown, CaretRight, Download, Archive, Info, XCircle, Lightbulb
} from '@phosphor-icons/react';
import { POLICY_THEMES, DOCUMENT_TYPES } from '@/components/library/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

// Policy Document Types
const POLICY_DOCUMENT_TYPES = [
  { id: 'Policy', label: 'Policy' },
  { id: 'Standard', label: 'Standard' },
  { id: 'Framework', label: 'Framework' },
  { id: 'Guideline', label: 'Guideline' },
  { id: 'Procedure', label: 'Procedure' },
  { id: 'Manual', label: 'Manual' }
];

// Status colors
const STATUS_COLORS = {
  draft: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  published: 'bg-green-500/20 text-green-400 border-green-500/30',
  superseded: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  archived: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const PolicyLibraryPage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [themeCounts, setThemeCounts] = useState({});
  
  // Filters
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Explore panel
  const [expandedThemes, setExpandedThemes] = useState({});

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = showArchived ? '/api/policy-library/archive' : '/api/policy-library/documents';
      const params = new URLSearchParams();
      if (selectedTheme) params.append('theme', selectedTheme);
      if (statusFilter !== 'all' && !showArchived) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}${endpoint}?${params.toString()}`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedTheme, statusFilter, searchQuery, showArchived]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/policy-library/stats`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  // Fetch theme counts
  const fetchThemeCounts = async () => {
    try {
      const response = await axios.get(`${API}/api/policy-library/theme-counts`);
      setThemeCounts(response.data.counts || {});
    } catch (error) {
      console.error('Failed to load theme counts');
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
    fetchThemeCounts();
  }, [fetchDocuments]);

  // Handle delete
  const handleDelete = async (docId) => {
    if (!confirm('Are you sure you want to delete this policy?')) return;
    try {
      await axios.delete(`${API}/api/policy-library/documents/${docId}`);
      toast.success('Policy deleted');
      fetchDocuments();
      fetchStats();
      fetchThemeCounts();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  // Handle archive
  const handleArchive = async (docId) => {
    if (!confirm('Are you sure you want to archive this policy?')) return;
    try {
      await axios.post(`${API}/api/policy-library/documents/${docId}/archive`);
      toast.success('Policy archived');
      fetchDocuments();
      fetchStats();
      fetchThemeCounts();
    } catch (error) {
      toast.error('Failed to archive policy');
    }
  };

  // View summary modal
  const viewSummary = (doc) => {
    setSelectedDocument(doc);
    setShowSummaryModal(true);
  };

  // Toggle theme expansion
  const toggleTheme = (themeId) => {
    setExpandedThemes(prev => ({
      ...prev,
      [themeId]: !prev[themeId]
    }));
  };

  // Filtered documents
  const filteredDocuments = useMemo(() => {
    return documents;
  }, [documents]);

  return (
    <div className="h-full flex flex-col" data-testid="policy-library-page">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border bg-surface">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold/10 rounded-lg">
              <Files size={28} className="text-gold" weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-semibold text-text-primary">Policy Library</h1>
              <p className="text-sm text-text-tertiary">Manage internal policies, standards, and frameworks</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="px-4 py-2 bg-surface-light border border-border text-text-secondary rounded-lg hover:bg-surface-lighter transition-colors flex items-center gap-2"
              data-testid="bulk-upload-btn"
            >
              <Upload size={18} /> Bulk Import
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-gold text-surface rounded-lg hover:bg-gold-hover transition-colors flex items-center gap-2"
              data-testid="add-policy-btn"
            >
              <Plus size={18} /> Add Policy
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Explore */}
        <div className="w-64 flex-shrink-0 border-r border-border bg-surface-light overflow-y-auto">
          {/* Stats */}
          <div className="p-4 border-b border-border">
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3">Stats</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-text-secondary">
                  <Files size={14} />
                  <span>Total</span>
                </div>
                <span className="font-medium text-text-primary">{stats?.total || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle size={14} weight="fill" />
                  <span>Published</span>
                </div>
                <span className="font-medium text-green-400">{stats?.published || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-amber-400">
                  <Clock size={14} weight="fill" />
                  <span>Draft</span>
                </div>
                <span className="font-medium text-amber-400">{stats?.draft || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-red-400">
                  <Warning size={14} weight="fill" />
                  <span>Overdue Review</span>
                </div>
                <span className="font-medium text-red-400">{stats?.overdue_review || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gold">
                  <Brain size={14} weight="fill" />
                  <span>NAVI Analyzed</span>
                </div>
                <span className="font-medium text-gold">{stats?.navi_analyzed || 0}</span>
              </div>
            </div>
          </div>

          {/* Themes */}
          <div className="p-4">
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-3">Explore</h4>
            <div className="space-y-1">
              {/* All Themes */}
              <button
                onClick={() => setSelectedTheme(null)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  !selectedTheme ? 'bg-gold/10 text-gold' : 'text-text-secondary hover:bg-surface-lighter'
                }`}
              >
                <span>All Themes</span>
                <span className="text-xs opacity-70">{stats?.total || 0}</span>
              </button>
              
              {/* Theme list */}
              {POLICY_THEMES.map(theme => {
                const ThemeIcon = theme.icon;
                const count = themeCounts[theme.id] || themeCounts[theme.label] || 0;
                const isSelected = selectedTheme === theme.id || selectedTheme === theme.label;
                
                return (
                  <button
                    key={theme.id}
                    onClick={() => setSelectedTheme(isSelected ? null : theme.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      isSelected ? 'bg-gold/10 text-gold' : 'text-text-secondary hover:bg-surface-lighter'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <ThemeIcon size={16} style={{ color: theme.color }} />
                      <span className="truncate">{theme.label}</span>
                    </div>
                    <span className="text-xs opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Archive Toggle */}
          <div className="p-4 border-t border-border">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                showArchived ? 'bg-amber-500/10 text-amber-400' : 'text-text-secondary hover:bg-surface-lighter'
              }`}
            >
              <div className="flex items-center gap-2">
                <Archive size={16} />
                <span>View Archived</span>
              </div>
            </button>
          </div>
        </div>

        {/* Right Panel - Documents */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Filter Bar */}
          <div className="flex-shrink-0 p-4 border-b border-border bg-surface flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {['all', 'draft', 'published', 'superseded'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    statusFilter === status
                      ? 'bg-gold text-surface'
                      : 'bg-surface-light text-text-secondary hover:bg-surface-lighter'
                  }`}
                >
                  {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search policies..."
                className="pl-9 pr-4 py-2 bg-surface-light border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold w-64"
              />
            </div>
          </div>

          {/* Archive Banner */}
          {showArchived && (
            <div className="flex-shrink-0 p-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-400">
                <Archive size={18} />
                <span className="text-sm font-medium">Viewing Archived Policies</span>
              </div>
              <button
                onClick={() => setShowArchived(false)}
                className="text-sm text-amber-400 hover:text-amber-300"
              >
                Exit Archive View
              </button>
            </div>
          )}

          {/* Documents Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-surface-light border-b border-border sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Policy</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Theme</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Effective</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Next Review</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-text-tertiary uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-tertiary">
                      <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                      Loading policies...
                    </td>
                  </tr>
                ) : filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-tertiary">
                      <Files size={48} className="mx-auto mb-2 opacity-50" />
                      <p>No policies found</p>
                      <button
                        onClick={() => setShowUploadModal(true)}
                        className="mt-3 text-sm text-gold hover:underline"
                      >
                        Add your first policy
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map(doc => {
                    const themeConfig = POLICY_THEMES.find(t => t.id === doc.theme || t.label === doc.theme) || POLICY_THEMES[0];
                    
                    // Calculate review status
                    let reviewStatus = null;
                    if (doc.next_review_date) {
                      const daysUntil = Math.ceil((new Date(doc.next_review_date) - new Date()) / (1000 * 60 * 60 * 24));
                      if (daysUntil < 0) {
                        reviewStatus = { text: `${Math.abs(daysUntil)} days overdue`, color: 'text-red-400' };
                      } else if (daysUntil <= 30) {
                        reviewStatus = { text: `${daysUntil} days`, color: 'text-amber-400' };
                      } else {
                        reviewStatus = { text: `${daysUntil} days`, color: 'text-text-tertiary' };
                      }
                    }
                    
                    return (
                      <tr key={doc.id} className="hover:bg-surface-light/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <FileText size={20} className="text-gold flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-text-primary truncate">{doc.title}</p>
                                {doc.summary && (
                                  <button
                                    onClick={() => viewSummary(doc)}
                                    className="p-1 text-gold hover:bg-gold/10 rounded transition-colors flex-shrink-0"
                                    title="View NAVI Summary"
                                  >
                                    <Lightbulb size={14} weight="fill" />
                                  </button>
                                )}
                              </div>
                              {doc.reference_number && (
                                <p className="text-xs text-text-tertiary font-mono">{doc.reference_number}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ backgroundColor: `${themeConfig.color}20`, color: themeConfig.color }}
                          >
                            {themeConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-surface-light rounded text-xs text-text-secondary">
                            {doc.document_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-text-tertiary" />
                            <span className="text-sm text-text-secondary">{doc.owner || 'Unassigned'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-text-secondary">
                            {doc.effective_date ? new Date(doc.effective_date).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm text-text-secondary">
                              {doc.next_review_date ? new Date(doc.next_review_date).toLocaleDateString() : '-'}
                            </span>
                            {reviewStatus && (
                              <p className={`text-xs ${reviewStatus.color}`}>{reviewStatus.text}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs border ${STATUS_COLORS[doc.status] || STATUS_COLORS.draft}`}>
                            {doc.status || 'draft'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {doc.pdf_path && (
                              <a
                                href={doc.pdf_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-text-tertiary hover:text-blue-400 transition-colors"
                                title="Download"
                              >
                                <Download size={16} />
                              </a>
                            )}
                            {!showArchived && (
                              <button
                                onClick={() => handleArchive(doc.id)}
                                className="p-1.5 text-text-tertiary hover:text-amber-400 transition-colors"
                                title="Archive"
                              >
                                <Archive size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(doc.id)}
                              className="p-1.5 text-text-tertiary hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchDocuments();
            fetchStats();
            fetchThemeCounts();
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            setShowBulkUploadModal(false);
            fetchDocuments();
            fetchStats();
            fetchThemeCounts();
          }}
        />
      )}

      {/* Summary Modal */}
      {showSummaryModal && selectedDocument && (
        <SummaryModal
          document={selectedDocument}
          onClose={() => {
            setShowSummaryModal(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================
// BULK UPLOAD MODAL COMPONENT
// ============================================
const BulkUploadModal = ({ onClose, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [theme, setTheme] = useState('');
  const [documentType, setDocumentType] = useState('Policy');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    // Filter for supported files
    const validFiles = selectedFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || 
      f.name.toLowerCase().endsWith('.doc') || 
      f.name.toLowerCase().endsWith('.docx')
    );
    setFiles(validFiles);
    
    if (validFiles.length !== selectedFiles.length) {
      toast.info(`${selectedFiles.length - validFiles.length} files skipped (only PDF/Word supported)`);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (files.length === 0 || !theme) {
      toast.error('Please select files and a theme');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('theme', theme);
      formData.append('document_type', documentType);
      formData.append('run_navi_analysis', 'true');
      
      const response = await axios.post(`${API}/api/policy-library/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      setResults(response.data);
      
      if (response.data.summary.successful > 0) {
        toast.success(`${response.data.summary.successful} policies uploaded successfully`);
      }
      if (response.data.summary.failed > 0) {
        toast.error(`${response.data.summary.failed} files failed to upload`);
      }
      
    } catch (error) {
      toast.error('Bulk upload failed');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Upload size={20} className="text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Bulk Import Policies</h3>
              <p className="text-xs text-gray-600">Upload multiple PDF/Word files at once</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        
        {results ? (
          <div className="p-4 bg-white">
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Upload Complete</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-2 bg-green-100 rounded">
                  <div className="text-2xl font-bold text-green-600">{results.summary.successful}</div>
                  <div className="text-xs text-gray-600">Successful</div>
                </div>
                <div className="p-2 bg-amber-100 rounded">
                  <div className="text-2xl font-bold text-amber-600">{results.summary.skipped}</div>
                  <div className="text-xs text-gray-600">Skipped</div>
                </div>
                <div className="p-2 bg-red-100 rounded">
                  <div className="text-2xl font-bold text-red-600">{results.summary.failed}</div>
                  <div className="text-xs text-gray-600">Failed</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {results.results.map((result, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    result.status === 'success' ? 'bg-green-50 border-green-200' :
                    result.status === 'skipped' ? 'bg-amber-50 border-amber-200' :
                    'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate">{result.filename}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      result.status === 'success' ? 'bg-green-100 text-green-700' :
                      result.status === 'skipped' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {result.status}
                    </span>
                  </div>
                  {result.reference_number && (
                    <div className="text-xs text-gray-600 mt-1">Ref: {result.reference_number}</div>
                  )}
                  {result.message && (
                    <div className="text-xs text-gray-600 mt-1">{result.message}</div>
                  )}
                  {result.missing_fields?.length > 0 && (
                    <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <Warning size={12} />
                      Missing: {result.missing_fields.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4 bg-white">
            {/* File Upload */}
            <div>
              <Label className="text-gray-900 font-medium">Select Files *</Label>
              <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 hover:border-teal-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  multiple
                  onChange={handleFilesChange}
                  className="hidden"
                  id="bulk-file-upload"
                />
                <label htmlFor="bulk-file-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <Upload size={32} className="text-gray-400 mb-2" />
                    <p className="text-sm text-gray-700">Click to select multiple files</p>
                    <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX files only</p>
                  </div>
                </label>
              </div>
            </div>
            
            {/* Selected Files List */}
            {files.length > 0 && (
              <div className="space-y-2">
                <Label className="text-gray-900 font-medium">Selected Files ({files.length})</Label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2 truncate">
                        <FileText size={16} className="text-teal-600 flex-shrink-0" />
                        <span className="text-sm text-gray-900 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          ({(file.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Theme Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-900 font-medium">Theme *</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger className="mt-1 bg-white border-gray-300">
                    <SelectValue placeholder="Select theme..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {POLICY_THEMES.map(t => (
                      <SelectItem key={t.id} value={t.id} className="text-gray-900 hover:bg-gray-100">
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-900 font-medium">Document Type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger className="mt-1 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {POLICY_DOCUMENT_TYPES.map(t => (
                      <SelectItem key={t.id} value={t.id} className="text-gray-900 hover:bg-gray-100">
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* NAVI Info */}
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Brain size={18} className="text-teal-600" />
                <span className="text-sm text-teal-700 font-medium">NAVI will analyze each file</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Automatically extracts title, summary, dates, and owner from each document
              </p>
            </div>
            
            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Uploading...</span>
                  <span className="text-teal-600 font-medium">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-teal-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || files.length === 0 || !theme}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <ArrowClockwise size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload {files.length} File{files.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================
// UPLOAD MODAL COMPONENT
// ============================================
const UploadModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    theme: '',
    document_type: 'Policy',
    version: '1.0',
    owner: '',
    effective_date: '',
    next_review_date: '',
    description: ''
  });
  const [naviExtraction, setNaviExtraction] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Handle file selection and NAVI pre-analysis
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setIsAnalyzing(true);
    setNaviExtraction(null);
    
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', selectedFile);
      
      const response = await axios.post(`${API}/api/policy-library/analyze-upload`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const extracted = response.data.extracted_fields;
        setNaviExtraction(response.data);
        
        // Auto-fill form with extracted data
        setFormData(prev => ({
          ...prev,
          title: extracted.title || prev.title || selectedFile.name.replace(/\.[^/.]+$/, ''),
          effective_date: extracted.effective_date || prev.effective_date,
          next_review_date: extracted.next_review_date || prev.next_review_date,
          owner: extracted.owner || prev.owner,
          description: extracted.summary || prev.description
        }));
      }
    } catch (error) {
      console.error('NAVI analysis failed:', error);
      // Continue without NAVI extraction
      setFormData(prev => ({
        ...prev,
        title: prev.title || selectedFile.name.replace(/\.[^/.]+$/, '')
      }));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle upload
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file || !formData.theme) {
      toast.error('Please select a file and theme');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('title', formData.title || file.name);
      uploadData.append('theme', formData.theme);
      uploadData.append('document_type', formData.document_type);
      uploadData.append('version', formData.version);
      if (formData.owner) uploadData.append('owner', formData.owner);
      if (formData.effective_date) uploadData.append('effective_date', formData.effective_date);
      if (formData.next_review_date) uploadData.append('next_review_date', formData.next_review_date);
      if (formData.description) uploadData.append('description', formData.description);
      uploadData.append('run_navi_analysis', 'true');
      
      const response = await axios.post(`${API}/api/policy-library/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Policy uploaded successfully');
        
        // Show warning for missing fields
        if (response.data.navi_analysis?.missing_fields?.length > 0) {
          const missing = response.data.navi_analysis.missing_fields;
          toast.info(`NAVI couldn't extract: ${missing.join(', ')}. Please update manually.`, {
            duration: 5000
          });
        }
        
        onSuccess();
      }
    } catch (error) {
      toast.error('Failed to upload policy');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-surface z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold/10 rounded-lg">
              <Upload size={20} className="text-gold" />
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">Upload Policy Document</h3>
              <p className="text-xs text-text-tertiary">NAVI will analyze and extract metadata</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-surface-light rounded">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* File Upload */}
          <div>
            <Label className="text-text-primary font-medium">Document File *</Label>
            <div className="mt-1 border-2 border-dashed border-border rounded-lg p-6 text-center bg-surface-light hover:border-gold/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleFileChange}
                className="hidden"
                id="policy-file-upload"
              />
              <label htmlFor="policy-file-upload" className="cursor-pointer">
                {isAnalyzing ? (
                  <div className="flex flex-col items-center">
                    <ArrowClockwise size={32} className="text-gold animate-spin mb-2" />
                    <p className="text-sm text-gold font-medium">NAVI is analyzing...</p>
                    <p className="text-xs text-text-tertiary mt-1">Extracting title, dates, and summary</p>
                  </div>
                ) : file ? (
                  <div className="flex flex-col items-center">
                    <FileText size={32} className="text-gold mb-2" />
                    <p className="text-sm text-text-primary font-medium">{file.name}</p>
                    <p className="text-xs text-text-tertiary mt-1">Click to change file</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload size={32} className="text-text-tertiary mb-2" />
                    <p className="text-sm text-text-secondary">Click to upload or drag & drop</p>
                    <p className="text-xs text-text-tertiary mt-1">PDF, DOC, DOCX, TXT up to 50MB</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* NAVI Extraction Info */}
          {naviExtraction && (
            <div className="p-3 bg-gold/5 border border-gold/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Brain size={18} className="text-gold" />
                <span className="text-sm font-medium text-gold">NAVI Extracted</span>
              </div>
              {naviExtraction.missing_fields?.length > 0 && (
                <div className="flex items-start gap-2 mt-2">
                  <Warning size={14} className="text-amber-400 mt-0.5" />
                  <p className="text-xs text-amber-400">
                    Please complete: {naviExtraction.missing_fields.join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Form Fields */}
          <div>
            <Label className="text-text-primary font-medium">Policy Title *</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="mt-1 bg-surface-light border-border"
              placeholder="Enter policy title"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-text-primary font-medium">Theme *</Label>
              <Select
                value={formData.theme}
                onValueChange={(value) => setFormData(prev => ({ ...prev, theme: value }))}
              >
                <SelectTrigger className="mt-1 bg-surface-light border-border">
                  <SelectValue placeholder="Select theme..." />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  {POLICY_THEMES.map(theme => (
                    <SelectItem key={theme.id} value={theme.id} className="text-text-primary hover:bg-surface-light">
                      {theme.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-text-primary font-medium">Document Type</Label>
              <Select
                value={formData.document_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, document_type: value }))}
              >
                <SelectTrigger className="mt-1 bg-surface-light border-border">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  {POLICY_DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id} className="text-text-primary hover:bg-surface-light">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-text-primary font-medium">
                Owner
                {naviExtraction?.missing_fields?.includes('owner') && (
                  <span className="text-amber-400 ml-1">(Required)</span>
                )}
              </Label>
              <Input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                className="mt-1 bg-surface-light border-border"
                placeholder="Policy owner name"
              />
            </div>
            <div>
              <Label className="text-text-primary font-medium">Version</Label>
              <Input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="mt-1 bg-surface-light border-border"
                placeholder="1.0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-text-primary font-medium">
                Effective Date
                {naviExtraction?.missing_fields?.includes('effective_date') && (
                  <span className="text-amber-400 ml-1">(Required)</span>
                )}
              </Label>
              <Input
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                className="mt-1 bg-surface-light border-border"
              />
            </div>
            <div>
              <Label className="text-text-primary font-medium">
                Next Review Date
                {naviExtraction?.missing_fields?.includes('next_review_date') && (
                  <span className="text-amber-400 ml-1">(Required)</span>
                )}
              </Label>
              <Input
                type="date"
                value={formData.next_review_date}
                onChange={(e) => setFormData(prev => ({ ...prev, next_review_date: e.target.value }))}
                className="mt-1 bg-surface-light border-border"
              />
            </div>
          </div>

          <div>
            <Label className="text-text-primary font-medium">
              Summary / Description
              {naviExtraction?.extracted_fields?.summary && (
                <span className="text-gold ml-1 text-xs">(NAVI generated)</span>
              )}
            </Label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-light border border-border rounded-lg text-text-primary focus:outline-none focus:border-gold h-24 resize-none"
              placeholder="Brief summary of the policy..."
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:bg-surface-light rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || !file || !formData.theme}
              className="px-4 py-2 bg-gold text-surface rounded-lg hover:bg-gold-hover transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <ArrowClockwise size={16} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload Policy
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// SUMMARY MODAL COMPONENT
// ============================================
const SummaryModal = ({ document, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-xl w-full max-w-lg">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold/10 rounded-lg">
              <Lightbulb size={20} className="text-gold" weight="fill" />
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">NAVI Summary</h3>
              <p className="text-xs text-text-tertiary truncate max-w-[280px]">{document.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-surface-light rounded">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="p-4 bg-surface-light rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Brain size={16} className="text-gold" />
              <span className="text-sm font-medium text-gold">AI-Generated Summary</span>
            </div>
            <p className="text-sm text-text-secondary leading-relaxed">
              {document.summary || 'No summary available for this policy.'}
            </p>
          </div>
          
          {/* Additional metadata */}
          <div className="mt-4 space-y-2">
            {document.effective_date && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-tertiary">Effective Date</span>
                <span className="text-text-primary">{new Date(document.effective_date).toLocaleDateString()}</span>
              </div>
            )}
            {document.next_review_date && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-tertiary">Next Review</span>
                <span className="text-text-primary">{new Date(document.next_review_date).toLocaleDateString()}</span>
              </div>
            )}
            {document.owner && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-tertiary">Owner</span>
                <span className="text-text-primary">{document.owner}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gold text-surface rounded-lg hover:bg-gold-hover transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyLibraryPage;
